import { useState } from 'react'
import type { JSX } from 'react'
import * as api from '../../api/client'
import type { ProjectMode } from '../../api/types'
import { countRecorded } from '../../game/order'
import type { PlayItem, RoleProgress } from '../../game/order'
import {
  DUCK_MAX_DB,
  DUCK_MIN_DB,
  originalGainFor,
  readDuckGainDb,
  readMixChoice,
  writeDuckGainDb,
  writeMixChoice,
} from '../../game/settings'
import type { MixChoice } from '../../game/settings'
import { useProjectRender } from '../../game/useRender'

export type PlayResultProps = {
  projectId: string
  title: string
  mode: ProjectMode
  items: PlayItem[]
  recordedIds: ReadonlySet<string>
  roles: RoleProgress[]
  ffmpegAvailable: boolean
  /** Режим replace без дорожки M&E — сервер откажет, предупреждаем заранее. */
  meMissing: boolean
  onReplayMissing: () => void
  onRestart: () => void
  onExit: () => void
}

export function PlayResult({
  projectId,
  title,
  mode,
  items,
  recordedIds,
  roles,
  ffmpegAvailable,
  meMissing,
  onReplayMissing,
  onRestart,
  onExit,
}: PlayResultProps): JSX.Element {
  const [choice, setChoice] = useState<MixChoice>(readMixChoice)
  const [duckGainDb, setDuckGainDb] = useState<number>(readDuckGainDb)
  const { render, starting, error, start, reset } = useProjectRender(projectId)

  const total = items.length
  const done = countRecorded(items, recordedIds)
  const skipped = total - done
  const replacing = mode === 'replace'

  const pickChoice = (next: MixChoice): void => {
    setChoice(next)
    writeMixChoice(next)
  }

  const pickDuck = (next: number): void => {
    setDuckGainDb(next)
    writeDuckGainDb(next)
  }

  const startRender = (): void => {
    // Режим mute отправляем ровно так же, как редактор: только originalGain,
    // чтобы запрос понимал и старый бэкенд (docs/API.md, «Рендер»).
    start(replacing ? { mode: 'replace' } : { originalGain: originalGainFor(choice, duckGainDb) })
  }

  const inProgress = render?.status === 'pending' || render?.status === 'running'
  const percent = Math.round((render?.progress ?? 0) * 100)

  let blockedReason: string | null = null
  if (done === 0) blockedReason = 'Нет ни одной записанной реплики — сводить нечего'
  else if (!ffmpegAvailable) blockedReason = 'На сервере нет ffmpeg — сборка недоступна'
  else if (replacing && meMissing) blockedReason = 'В режиме замены нужна дорожка M&E'

  return (
    <div className="play-page">
      <div className="row wrap" style={{ marginBottom: 4 }}>
        <button type="button" className="ghost" onClick={onExit}>
          ← В редактор
        </button>
        <h1 style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</h1>
        <span className="spacer" />
        <span className="badge">итог</span>
      </div>

      <section className="panel">
        <header>
          <h2>{skipped === 0 ? 'Озвучено всё' : 'Что получилось'}</h2>
        </header>
        <div className="play-summary">
          <div className="play-stat">
            <b style={{ color: 'var(--success)' }}>{done}</b>
            <span className="faint">записано</span>
          </div>
          <div className="play-stat">
            <b style={{ color: skipped > 0 ? 'var(--warning)' : undefined }}>{skipped}</b>
            <span className="faint">пропущено</span>
          </div>
          <div className="play-stat">
            <b>{total}</b>
            <span className="faint">реплик всего</span>
          </div>
        </div>

        <div className="play-roles">
          {roles.map((role) => (
            <div key={role.roleId} className="play-role-row">
              <span className="role-swatch" style={{ background: role.color }} />
              <span style={{ flex: 1, minWidth: 0 }}>{role.name}</span>
              <span className="mono faint">
                {role.done} / {role.total}
              </span>
              <div className="progress" style={{ width: 120 }}>
                <i style={{ width: `${role.total > 0 ? (role.done / role.total) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
        </div>

        {skipped > 0 ? (
          <div className="row wrap" style={{ marginTop: 12 }}>
            <button type="button" onClick={onReplayMissing}>
              ▶ Пройти пропущенные ({skipped})
            </button>
            <span className="faint">
              Пропущенные реплики просто остались без дубля — в сведении их не будет.
            </span>
          </div>
        ) : null}
      </section>

      <section className="panel">
        <header>
          <h2>Сведение</h2>
        </header>

        {replacing ? (
          <div className="stack">
            <p className="muted" style={{ margin: 0 }}>
              Проект собирается поверх дорожки M&E: музыка и эффекты идут непрерывно, оригинальные
              реплики не участвуют вообще. Выбирать, глушить оригинал или нет, здесь незачем —
              глушить нечего.
            </p>
            {meMissing ? (
              <div className="notice warn">
                <strong>Дорожки M&E нет</strong>
                <span className="faint">
                  Сервер откажет в сборке. Назначьте тип «M&E» одной из дорожек проекта в редакторе
                  или загрузите её отдельным файлом.
                </span>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="stack">
            <div className="play-options">
              <button
                type="button"
                className={`play-option${choice === 'mute' ? ' selected' : ''}`}
                onClick={() => pickChoice('mute')}
                aria-pressed={choice === 'mute'}
              >
                <strong>Убрать оригинал</strong>
                <span className="faint">
                  Слышны только записанные голоса. Чистый результат — и слышно каждую дыру там, где
                  реплика пропущена.
                </span>
              </button>
              <button
                type="button"
                className={`play-option${choice === 'duck' ? ' selected' : ''}`}
                onClick={() => pickChoice('duck')}
                aria-pressed={choice === 'duck'}
              >
                <strong>Приглушить оригинал</strong>
                <span className="faint">
                  Оригинал остаётся фоном, поверх — свои голоса. Слышно, что говорили на самом деле,
                  и пропущенные реплики не превращаются в тишину.
                </span>
              </button>
            </div>

            {choice === 'duck' ? (
              <div>
                <label htmlFor="play-duck">
                  Громкость оригинала: <span className="mono">{duckGainDb} дБ</span>
                </label>
                <input
                  id="play-duck"
                  type="range"
                  min={DUCK_MIN_DB}
                  max={DUCK_MAX_DB}
                  step={1}
                  value={duckGainDb}
                  onChange={(e) => pickDuck(Number(e.currentTarget.value))}
                />
              </div>
            ) : null}
          </div>
        )}

        <div className="row wrap" style={{ marginTop: 12 }}>
          <button
            type="button"
            className="primary play-main"
            disabled={starting || inProgress || blockedReason !== null}
            onClick={startRender}
            title={blockedReason ?? 'Собрать видео с записанными голосами'}
          >
            {inProgress ? 'Собираю…' : '⚙ Собрать видео'}
          </button>
          {blockedReason ? <span className="faint">{blockedReason}</span> : null}
        </div>

        {error ? <p className="faint">{error}</p> : null}

        {render ? (
          <div className="stack" style={{ marginTop: 12 }}>
            <div className="progress">
              <i style={{ width: `${render.status === 'done' ? 100 : percent}%` }} />
            </div>
            <div className="row wrap">
              <span className="faint">
                {render.status === 'pending' ? 'В очереди…' : null}
                {render.status === 'running' ? `Готово ${percent}%` : null}
                {render.status === 'done' ? 'Готово' : null}
                {render.status === 'error' ? `Ошибка: ${render.error ?? 'неизвестно'}` : null}
              </span>
              <span className="spacer" />
              {render.status === 'error' ? (
                <button type="button" className="ghost" onClick={reset}>
                  Попробовать снова
                </button>
              ) : null}
            </div>

            {render.status === 'done' ? (
              <>
                <div className="video-shell">
                  <video src={api.renderUrl(render.id)} controls preload="metadata" playsInline />
                </div>
                <div className="row wrap">
                  <a
                    className="badge"
                    href={api.renderUrl(render.id)}
                    download
                    style={{ color: 'var(--accent)' }}
                  >
                    ⤓ Скачать MP4
                  </a>
                  <span className="spacer" />
                  <button type="button" onClick={onRestart}>
                    ↻ Играть заново
                  </button>
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </section>

      <div className="row wrap">
        <button type="button" onClick={onRestart}>
          ↻ Играть заново
        </button>
        <button type="button" className="ghost" onClick={onExit}>
          В редактор
        </button>
      </div>
    </div>
  )
}
